import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { uploadBulkInvoice } from "../../Actions/InvoicesAction";
import { getCompany } from "../../Actions/CompanyAction";
import {
  globalStyle,
  handleDangerVisible,
  handleVisible,
} from "../../global/Utils";
import { CircularProgress, IconButton } from "@mui/material";
import * as XLSX from "xlsx";
import CloseIcon from "@mui/icons-material/Close";
import DownloadTemplateModal from "./DownloadTemplateModal";

import { upperCaseInput } from "../../global/textCase";
import { SearchableSelect } from "../Common/SearchableSelect";
const BulkInvoiceUploader = () => {
  const dispatch = useDispatch();
  const formRef = React.useRef(null);
  const [formData, setFormData] = useState({
    companyId: "",
    file: null,
  });
  const [errors, setErrors] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const { companiesList, isGettingCompanyList } = useSelector(
    (state) => state.CompanyListReducer,
  );
  const { isUploadingBulkInvoice } = useSelector(
    (state) => state.InvoicesListReducer,
  );
  /* ------------------ HANDLE INPUT CHANGE ------------------ */
  const handleChange = (e) => {
    const { name, value } = upperCaseInput(e.target);
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const validTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];
    if (!validTypes.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        file: "Only Excel files (.xls, .xlsx) are allowed",
      }));
      return;
    }
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        XLSX.read(data, { type: "array" }); // just to validate file
        setFormData((prev) => ({ ...prev, file }));
        setErrors((prev) => ({ ...prev, file: "" }));
      } catch (err) {
        setErrors((prev) => ({
          ...prev,
          file: "Invalid Excel file",
        }));
      }
    };
    reader.readAsArrayBuffer(file);
  };
  /* ------------------ VALIDATION ------------------ */
  const validate = () => {
    const newErrors = {};
    if (!formData.companyId) {
      newErrors.companyId = "Company is required";
    }
    if (!formData.file) {
      newErrors.file = "Excel file is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleRemoveFile = () => {
    setFormData((prev) => ({ ...prev, file: null }));
  };
  /* ------------------ SUBMIT ------------------ */
  const handleSubmit = (e, type) => {
    e.preventDefault();
    if (!validate()) {
      setTimeout(() => {
        const firstError = document.querySelector(".border-red-500");
        if (firstError) {
          firstError.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          firstError.focus();
        }
      }, 100);
      return;
    }
    const payload = new FormData();
    payload.append("companyId", formData.companyId);
    payload.append("file", formData.file);
    payload.append("saveInvoiceStatus", type);
    dispatch(
      uploadBulkInvoice(
        payload,
        setFormData,
        handleVisible,
        handleDangerVisible,
      ),
    );
  };
  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(getCompany("", "", ""));
    }, 500);
    return () => clearTimeout(timer);
  }, [dispatch]);
  return (
    <>
      <div className="w-full min-h-screen flex justify-center py-4">
        <form
          ref={formRef}
          className="w-full space-y-6"
        >
          <div className="flex items-center justify-between">
            <h2
              className="font-bold text-xl"
              style={{
                fontFamily: globalStyle.fontFamilyHeading,
                color: globalStyle.curveDarkColor,
              }}
            >
              Upload Bulk Invoice
            </h2>
            <button
              type="button"
              onClick={() => setOpenModal(true)}
              className="px-4 py-2 rounded-md text-white font-semibold"
              style={{ background: globalStyle.buttonColor }}
            >
              Download Template
            </button>
          </div>
          {isUploadingBulkInvoice ? (
            <div className="flex justify-center items-center h-20">
              <CircularProgress />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {isGettingCompanyList ? (
                <div className="flex justify-center items-center h-20">
                  <CircularProgress />
                </div>
              ) : (
                <div>
                  <label className="block mb-1 font-medium">
                    Company Name<span className="text-red-500">*</span>
                  </label>
                  <SearchableSelect
                    name="companyId"
                    value={formData.companyId}
                    onChange={handleChange}
                    disabled={!companiesList || companiesList.length === 0}
                    className={`uppercase w-full px-4 py-2 border rounded-md ${
                      errors.companyId ? "border-red-500" : "border-gray-300"
                    }`}
                  >
                    {!companiesList || companiesList.length === 0 ? (
                      <option value="">No Companies Available</option>
                    ) : (
                      <>
                        <option value="">Select</option>
                        {companiesList.map((company) => (
                          <option key={company._id} value={company._id}>
                            {company.companyName} / {company?.ntn}
                          </option>
                        ))}
                      </>
                    )}
                  </SearchableSelect>
                  {errors.companyId && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.companyId}
                    </p>
                  )}
                </div>
              )}
              <div>
                <label className="block mb-1 font-medium">
                  Upload Excel<span className="text-red-500">*</span>
                </label>
                {!formData.file ? (
                  <input
                    type="file"
                    accept=".xls,.xlsx,.xlsm"
                    onChange={handleFileChange}
                    className={`w-full px-3 py-2 border rounded-md ${
                      errors.file ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                ) : (
                  <div className="flex items-center justify-between border rounded-md px-3 py-2 bg-gray-50">
                    <span className="text-sm truncate">
                      {formData.file.name}
                    </span>
                    <IconButton size="small" onClick={handleRemoveFile}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </div>
                )}
                {errors.file ? (
                  <p className="text-red-500 text-sm mt-1">{errors.file}</p>
                ) : (
                  <p className="text-gray-500 text-sm mt-1">
                    Accepted formats: .xls, .xlsx, .xlsm
                  </p>
                )}
              </div>
            </div>
          )}
          <div class="bg-blue-50 border border-blue-200 text-blue-900 rounded-lg p-4">
            <h6 class="font-semibold text-sm mb-2">File Format Requirements</h6>
            <ul class="list-disc pl-5 space-y-1 text-sm">
              <li>File must be in Excel format</li>
              <li>
                Follow the instructions mentioned in the excel template file
              </li>
              <li>Data for columns with green background color is required</li>
              <li>Valid values should be selected from drop downs</li>
              <li>Your data should start from row number 5</li>
              <li>Date format: Day/Month/Year (e.g., 7/4/2025)</li>
            </ul>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              type="button"
              disabled={isUploadingBulkInvoice}
              onClick={(e) => handleSubmit(e, 1)}
              className="w-full md:w-1/2 mx-auto block py-2 rounded-md text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400"
              style={{ background: globalStyle.buttonColor }}
            >
              {isUploadingBulkInvoice ? "Please Wait..." : "Save Only"}
            </button>
            <button
              type="button"
              disabled={isUploadingBulkInvoice}
              onClick={(e) => handleSubmit(e, 2)}
              className="w-full md:w-1/2 mx-auto block py-2 rounded-md text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400"
              style={{ background: globalStyle.buttonColor }}
            >
              {isUploadingBulkInvoice ? "Please Wait..." : "Save & Post To FBR"}
            </button>
          </div>
        </form>
        <DownloadTemplateModal
          isOpen={openModal}
          onClose={() => setOpenModal(false)}
        />
      </div>
    </>
  );
};

export default BulkInvoiceUploader;
